<script lang="ts" setup>
import { Comment, computed, inject, nextTick, onMounted, ref, useSlots } from 'vue'
import { ListboxButton } from '@headlessui/vue'
import LineChevronDown from '../../icons/LineChevronDown.vue'
import type { FieldGroupProvider, FieldProvider, FieldSelectProvider } from '../../types/field'
import { DTFieldGroupProvider, DTFieldProvider, DTFieldSelectProvider } from '../../keys'
import type { DTSelectOption, FieldInputSizes } from './types'

const props = withDefaults(defineProps<{
  value?: DTSelectOption | DTSelectOption[]
  placeholder?: string
  disabled?: boolean
  open?: boolean
  sizeVariant?: keyof typeof FieldInputSizes
}>(), {})

const button = ref<InstanceType<typeof ListboxButton>>()
const labelProvider = inject<FieldProvider | undefined>(DTFieldProvider, undefined)
const groupProvider = inject<FieldGroupProvider | undefined>(DTFieldGroupProvider, undefined)
const selectProvider = inject<FieldSelectProvider>(DTFieldSelectProvider)

onMounted(() => {
  nextTick(() => {
    selectProvider?.registerFieldSelectButton(button.value?.$el)
  })
})

const inputSizeClass = computed<string>(() => {
  const size = props.sizeVariant ?? groupProvider?.size ?? labelProvider?.size
  switch (size) {
    case 'sm':
      return 'py-2 px-3 text-sm'
    case 'md':
    default:
      return 'text-xs sm:text-sm py-2 px-3 md:text-base md:py-2.5 md:px-3.5'
  }
})

const selectedOptionText = computed(() => {
  if (Array.isArray(props.value))
    return props.value.map(opt => opt?.text).filter(Boolean).join(', ')

  return props.value?.text
})

const slotIsEmpty = computed(() => {
  const slot = useSlots()?.default?.({})
  return !slot || (Array.isArray(slot) ? slot : []).every(vnode => vnode.type === Comment)
})

const inputClass = computed<string[]>(() => {
  const cls = [
    'focus:outline-none',
    'rounded-lg',
    'w-full',
    'text-left',
  ]

  if (props.disabled)
    cls.push('text-gray-800/40 resize-none')

  else if (!selectedOptionText.value && slotIsEmpty)
    cls.push('text-gray-400')

  else
    cls.push('text-gray-800')

  cls.push(inputSizeClass.value)
  return cls
})
</script>

<template>
  <ListboxButton
    ref="button"
    :class="inputClass"
    @click.stop
  >
    <div class="flex items-center justify-between">
      <slot>
        <span class="truncate">{{ selectedOptionText || props.placeholder }}</span>
      </slot>
      <LineChevronDown
        class="size-5 shrink-0"
        :class="[disabled ? 'text-gray-300' : 'text-gray-500']"
        aria-hidden="true"
      />
    </div>
  </ListboxButton>
</template>
